import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedCurrencies, withStyles } from 'vopay';
import { BodyText, Button, Card, MaterialSymbol, Title } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import styles from './NoTransferCard.module.scss';

const cn = withStyles(styles);

type Point = {
  icon: ComponentProps<typeof MaterialSymbol>['icon'];
  text: string;
};

type NoTransferCardProps = {
  currency?: SupportedCurrencies;
  /**
   * False when the currency has no default operational bank account: a schedule would have nowhere to pay out, so
   * the card sends the user to create one instead.
   */
  hasOperationalAccount: boolean;
  /** Omitted when the user lacks the permission to set up a transfer, which hides the button. */
  onSetUpClick?: () => void;
};

export default function NoTransferCard({ currency, hasOperationalAccount, onSetUpClick }: NoTransferCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.NoTransferCard' });

  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const points: Point[] = [
    {
      icon: 'cached',
      text: t('Points.moveBalance', {
        defaultValue: 'Move your available balance to your bank account on a schedule.',
      }),
    },
    {
      icon: 'schedule',
      text: t('Points.frequency', {
        defaultValue: 'Run it daily, weekly, bi-weekly or monthly, starting on a date you pick.',
      }),
    },
    {
      icon: 'do_not_disturb_on',
      text: t('Points.minimumBalance', {
        defaultValue: 'Set the balance that triggers a transfer — reach it and the full balance goes out.',
      }),
    },
  ];

  const title = currency
    ? t('titleCurrency', { defaultValue: 'No automated {{currency}} transfer set up', currency })
    : t('title', { defaultValue: 'No automated transfer set up' });

  let description: string;

  if (hasOperationalAccount) {
    description = currency
      ? t('descriptionCurrency', {
          defaultValue:
            'Set up an automated transfer of funds from your {{currency}} VoPay balance to your bank account. You can still transfer funds manually at any time.',
          currency,
        })
      : t('description', {
          defaultValue:
            'Set up an automated transfer of funds from your VoPay balance to your bank account. You can still transfer funds manually at any time.',
        });
  } else if (clientAccountsEnabled) {
    // Client-accounts users pay out to the primary client account and have no Operational Accounts page to add one.
    description = currency
      ? t('noClientAccountBankAccountCurrency', {
          defaultValue:
            "A schedule pays out to the default {{currency}} bank account of your primary client account, and there isn't one yet.",
          currency,
        })
      : t('noClientAccountBankAccount', {
          defaultValue:
            "A schedule pays out to the default bank account of your primary client account, and there isn't one yet.",
        });
  } else {
    description = currency
      ? t('noOperationalAccountCurrency', {
          defaultValue:
            "A schedule pays out to your default {{currency}} operational account, and there isn't one yet. Add it first, then come back to set the transfer up.",
          currency,
        })
      : t('noOperationalAccount', {
          defaultValue:
            "A schedule pays out to your default operational account, and there isn't one yet. Add it first, then come back to set the transfer up.",
        });
  }

  return (
    <Card hoverEffect={false} className={cn('card')} data-testid="auto-balance-transfer-empty">
      <div className={cn('icon')}>
        <MaterialSymbol icon="cached" size="32px" />
      </div>

      <Title level="2">{title}</Title>
      <BodyText className={cn('description')}>{description}</BodyText>

      <ul className={cn('points')}>
        {points.map(({ icon, text }) => (
          <li key={icon} className={cn('point')}>
            <MaterialSymbol icon={icon} size="20px" />
            <BodyText size="sm">{text}</BodyText>
          </li>
        ))}
      </ul>

      {onSetUpClick && (
        <Button id="auto-balance-transfer-set-up" colorScheme="primary" iconLeft="add" onClick={onSetUpClick}>
          {hasOperationalAccount
            ? t('setUpTransferButton', { defaultValue: 'Set Up Transfer' })
            : t('setUpOperationalAccountButton', { defaultValue: 'Set Up Operational Account' })}
        </Button>
      )}
    </Card>
  );
}
